import { CSSProperties, useId } from "react";

interface RubberStampProps {
  /** Top orbit text (upper arc, reads left-to-right). */
  label?: string;
  /** Bottom orbit text (lower arc, reads left-to-right). */
  sub?: string;
  /** Disc fill color. Cream text is layered on top. */
  color?: string;
  size?: number;
  style?: CSSProperties;
}

// ─────────────────────────────────────────────────────────────────────────────
// RubberStamp — the FIG verified seal (Round Seal · Direction 01).
// Solid rust disc with the lowercase italic "fig" wordmark in the center and
// orbiting tracked-uppercase type around the rim. Label arcs across the top,
// sub arcs across the bottom. Used everywhere the app stamps a scan as
// verified / vintage / sold / posted — the visual is intentionally consistent
// so any "FIG seal" reads instantly as the same brand mark.
// ─────────────────────────────────────────────────────────────────────────────
export function RubberStamp({
  label = "VERIFIED",
  sub = "",
  color = "hsl(var(--rust))",
  size = 130,
  style,
}: RubberStampProps) {
  // Unique IDs so multiple stamps on a page don't clash on the SVG defs.
  const uid = useId().replace(/[:]/g, "");
  const topPathId = `stamp-top-${uid}`;

  // Geometry, all in the 130-unit viewBox so font sizes stay proportional.
  const cx = 65;
  const cy = 65;
  const rOuter = 60;     // outer hairline
  const rDisc = 56;      // solid colored disc
  const rOrbit = 47;     // baseline radius for the orbiting text ring
  const rInnerRule = 39; // thin inner hairline that frames the wordmark

  // Two arc paths the text follows. Top arc sweeps clockwise across the upper
  // half so the label reads upright; bottom arc sweeps counter-clockwise so
  // the sub text is also upright (rotated 180° trick — start on the right,
  // end on the left, with sweep-flag 0).
  const topArc = `M ${cx - rOrbit} ${cy} A ${rOrbit} ${rOrbit} 0 0 1 ${cx + rOrbit} ${cy}`;

  const orbitFontSize = size * 0.078; // tracked uppercase, sized to the rim

  return (
    <div
      style={{
        width: size,
        height: size,
        position: "relative",
        color,
        transform: "rotate(-8deg)",
        animation: "fig-stamp 0.5s ease-out both",
        ...style,
      }}
    >
      <svg
        viewBox="0 0 130 130"
        style={{ width: "100%", height: "100%", position: "absolute", inset: 0, overflow: "visible" }}
      >
        <defs>
          <filter id={`inky-${uid}`}>
            <feTurbulence baseFrequency="0.9" numOctaves="2" />
            <feDisplacementMap in="SourceGraphic" scale="1.4" />
          </filter>
          <path id={topPathId} d={topArc} />
        </defs>

        {/* Solid colored disc — the brand mark surface. */}
        <circle cx={cx} cy={cy} r={rDisc} fill="currentColor" />
        {/* Outer hairline, slightly distressed by the inky displacement. */}
        <circle
          cx={cx}
          cy={cy}
          r={rOuter}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          filter={`url(#inky-${uid})`}
        />
        {/* Inner hairline framing the wordmark — cream on the rust disc. */}
        <circle
          cx={cx}
          cy={cy}
          r={rInnerRule}
          fill="none"
          stroke="hsl(var(--cream))"
          strokeOpacity="0.55"
          strokeWidth="0.75"
        />

        {/* Orbiting type — top arc */}
        {label && (
          <text
            fill="hsl(var(--cream))"
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 600,
              fontSize: orbitFontSize,
              letterSpacing: `${orbitFontSize * 0.28}px`,
              textTransform: "uppercase",
            }}
          >
            <textPath href={`#${topPathId}`} startOffset="50%" textAnchor="middle">
              {label}
            </textPath>
          </text>
        )}
      </svg>

      {/* Lowercase italic "fig" wordmark with the sub label tucked beneath
          it — both centered inside the inner hairline. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          gap: size * 0.02,
        }}
      >
        <span
          className="fig-wordmark"
          style={{
            color: "hsl(var(--cream))",
            fontSize: size * 0.42,
            lineHeight: 0.85,
          }}
        >
          fig
        </span>
        {sub && (
          <span
            style={{
              color: "hsl(var(--cream))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 600,
              fontSize: size * 0.085,
              letterSpacing: `${size * 0.02}px`,
              textTransform: "uppercase",
              lineHeight: 1,
            }}
          >
            {sub}
          </span>
        )}
      </div>
    </div>
  );
}
